import React, { useEffect, useState } from 'react';
import { fetchProducts, fetchCategories } from './api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { 
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  IconButton,
  Button,
  useTheme,
  Modal,
  Backdrop,
  Fade,
  Chip,
  Tooltip,
  Skeleton,
  useMediaQuery,
  Snackbar,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency, getCurrencyDecimals, calculateDiscountPercentage } from './currencyHelpers';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { addFavorite, removeFavorite, getFavoriteIds } from './favoritesApi';
import config from './config';
import { useTranslation } from 'react-i18next';
import { isBranchOpen } from './branchUtils';
import ClosedMessage from './ClosedMessage';
import ShareIcon from '@mui/icons-material/Share';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import { QRCodeCanvas } from 'qrcode.react';

// ✨ Animations
const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const heartBeat = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.3); }
  50% { transform: scale(1.1); }
  75% { transform: scale(1.2); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideTransition = keyframes`
  0% { opacity: 0; transform: translateX(30px); }
  100% { opacity: 1; transform: translateX(0); }
`;

function Products() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null); // Selected color for current product
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [autoSlideInterval, setAutoSlideInterval] = useState(null);
  
  const { selectedBranch } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const { t } = useTranslation();
  const [branchStatus, setBranchStatus] = useState({ isOpen: true, nextOpen: '' });
  // ✅ QR Modal state
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedProductForQR, setSelectedProductForQR] = useState(null);

  // ✅ Open external product URL helper
  const handleOpenExternalProduct = (prod, e) => {
    if (e) e.stopPropagation();
    const productId = prod.Id || prod.id;
    const url = `https://www.elorayem.com/product/${productId}`;
    window.open(url, '_blank');
  };

  // ✅ Generate Product Link
  const generateProductLink = (productId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/product/${productId}`;
  };

  // ✅ Copy Link with Fallback
  const handleCopyProductLink = (product) => {
    const link = generateProductLink(product.Id || product.id);
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        showSnackbar(t('product.linkCopied', 'Product link copied!'), 'success');
      }).catch(() => {
        fallbackCopyToClipboard(link);
      });
    } else {
      fallbackCopyToClipboard(link);
    }
  };

  // ✅ Fallback Copy Method
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      showSnackbar(t('product.linkCopied', 'Product link copied!'), 'success');
    } catch (err) {
      showSnackbar(t('errors.copyFailed', 'Failed to copy link'), 'error');
    }
    
    document.body.removeChild(textArea);
  };

  // ✅ Share via Web Share API
  const handleShareProduct = async (product) => {
    const link = generateProductLink(product.Id || product.id);
    const title = product.ProductName;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `${t('product.checkOut', 'Check out')} ${title}!`,
          url: link,
        });
        showSnackbar(t('product.shared', 'Shared successfully!'), 'success');
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyProductLink(product);
        }
      }
    } else {
      handleCopyProductLink(product);
    }
  };

  // ✅ Open QR Modal
  const handleOpenProductQRModal = (product, e) => {
    if (e) e.stopPropagation();
    setSelectedProductForQR(product);
    setQrModalOpen(true);
  };

  // ✅ Download QR Code
  const handleDownloadProductQR = () => {
    const canvas = document.getElementById('product-qr-code-canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedProductForQR?.ProductName || 'product'}-qr-code.png`;
      link.click();
      showSnackbar(t('product.qrDownloaded', 'QR Code downloaded!'), 'success');
    }
  };

  // ✅ Share to WhatsApp
  const handleShareProductWhatsApp = (product) => {
    const link = generateProductLink(product.Id || product.id);
    const text = encodeURIComponent(`${t('product.checkOut', 'Check out')} ${product.ProductName}! ${link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // ✅ Share to Facebook
  const handleShareProductFacebook = (product) => {
    const link = generateProductLink(product.Id || product.id);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, '_blank');
  };

  // ✅ Share to Twitter
  const handleShareProductTwitter = (product) => {
    const link = generateProductLink(product.Id || product.id);
    const text = encodeURIComponent(`${t('product.checkOut', 'Check out')} ${product.ProductName}!`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(link)}`, '_blank');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadFavorites = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const ids = await getFavoriteIds(user.Id || user.id, selectedBranch?.id);
        setFavoriteIds(ids);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      setBranchStatus(isBranchOpen(selectedBranch));
    }
  }, [selectedBranch]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchProducts(selectedBranch?.id, selectedCurrency).then(setProducts),
          fetchCategories(selectedBranch?.id).then(setCategories)
        ]);
        await loadFavorites();
      } catch (error) {
        console.error('Error loading data:', error);
        showSnackbar(t('errors.loadingFailed', 'Failed to load products'), 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedBranch, selectedCurrency, t]);

  // ✅ NEW: Auto-open product from deep link
  useEffect(() => {
    const checkPendingProduct = () => {
      const pendingId = localStorage.getItem('pendingProductId');
      
      if (pendingId && products.length > 0 && !loading && selectedBranch) {
        const product = products.find(p => 
          String(p.Id) === String(pendingId) || 
          String(p.id) === String(pendingId)
        );
        
        if (product) {
          localStorage.removeItem('pendingProductId');
          setTimeout(() => {
            handleProductClick(product);
            showSnackbar(`📦 ${product.ProductName}`, 'success');
          }, 300);
        } else {
          localStorage.removeItem('pendingProductId');
          showSnackbar(t('errors.productNotFound', 'Product not found in this branch'), 'error');
        }
      }
    };

    if (products.length > 0 && !loading && selectedBranch) {
      checkPendingProduct();
    }
  }, [products, loading, selectedBranch, t]);

  // ✅ Auto-slide effect
  useEffect(() => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      
      if (images.length > 1) {
        const interval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        
        setAutoSlideInterval(interval);
        
        return () => {
          clearInterval(interval);
        };
      }
    } else {
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      }
    }
  }, [selectedProduct]);

  const toggleFavorite = async (productId) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showSnackbar(t('auth.loginRequired', 'Please login first'), 'warning');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.Id || user.id;

      if (favoriteIds.includes(productId)) {
        await removeFavorite(userId, productId, selectedBranch?.id);
        setFavoriteIds(prev => prev.filter(id => id !== productId));
        showSnackbar(t('favorites.removed', 'Removed from favorites'), 'info');
      } else {
        await addFavorite(userId, productId, selectedBranch?.id);
        setFavoriteIds(prev => [...prev, productId]);
        showSnackbar(t('favorites.added', 'Added to favorites'), 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showSnackbar(t('errors.favoriteError', 'Could not update favorites'), 'error');
    }
  };

  const handleProductClick = (prod) => {
    setSelectedProduct(prod);
    setCurrentImageIndex(0);
    setSelectedColor(null);
  };

  const handleImageClick = (imageUrl, e) => {
    e.stopPropagation();
    setSelectedImage(imageUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setCurrentImageIndex(0);
    setSelectedColor(null);
    if (autoSlideInterval) {
      clearInterval(autoSlideInterval);
      setAutoSlideInterval(null);
    }
  };

  // ✅ FIXED: Get all product images - handle thumbnail/medium/large structure
  const getAllProductImages = (prod) => {
    const images = [];
    
    const mainImageUrls = getImageUrls(prod);
    if (mainImageUrls && mainImageUrls.large) {
      images.push(mainImageUrls.large);
    }

    if (prod.ProductImages && Array.isArray(prod.ProductImages) && prod.ProductImages.length > 0) {
      prod.ProductImages.forEach((img) => {
        let imgUrl = null;
        if (typeof img === 'object' && img !== null) {
          imgUrl = img.large || img.medium || img.thumbnail;
          if (imgUrl) images.push(imgUrl);
        } else if (typeof img === 'string') {
          if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:image')) {
            imgUrl = img;
          } else if (img.startsWith('/')) {
            imgUrl = `${config.SITE_BASE_URL}${img}`;
          } else {
            imgUrl = `${config.SITE_BASE_URL}/images/${img}`;
          }
          if (imgUrl) images.push(imgUrl);
        }
      });
    }
    return images;
  };

  const handleNextImage = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const handlePrevImage = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const toggleAutoSlide = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      } else {
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const getImageUrls = (prod) => {
    const imageUrl = prod.ImageUrl;
    const fallbackUrl = '/logo-storesye.png';
    
    if (!imageUrl) return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
    
    if (typeof imageUrl === 'object' && imageUrl.thumbnail) {
      return {
        thumbnail: imageUrl.thumbnail.startsWith('http') 
          ? imageUrl.thumbnail 
          : `${config.SITE_BASE_URL}${imageUrl.thumbnail}`,
        medium: imageUrl.medium.startsWith('http') 
          ? imageUrl.medium 
          : `${config.SITE_BASE_URL}${imageUrl.medium}`,
        large: imageUrl.large.startsWith('http') 
          ? imageUrl.large 
          : `${config.SITE_BASE_URL}${imageUrl.large}`
      };
    }
    
    if (typeof imageUrl === 'string') {
      let fullUrl;
      if (imageUrl.startsWith('http') || imageUrl.startsWith('data:image')) {
        fullUrl = imageUrl;
      } else if (imageUrl.startsWith('/images/')) {
        fullUrl = `${config.SITE_BASE_URL}${imageUrl}`;
      } else {
        fullUrl = imageUrl;
      }
      return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
    }
    
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  };

  const filteredCategories = categories
    .filter(cat => !/^(العروض:|offer|deal|مجموعة:)/i.test(cat.CategoryName))
    .sort((a, b) => {
      const orderA = a.CategoriesOrder ?? 9999;
      const orderB = b.CategoriesOrder ?? 9999;
      return orderA - orderB;
    });

  const productsByCategory = filteredCategories.map(cat => ({
    ...cat,
    products: products.filter(p => p.CategoryId === cat.Id && (!Array.isArray(p.SubProducts) || p.SubProducts.length === 0))
  }));

  if (!branchStatus.isOpen) {
    return <ClosedMessage openTime={branchStatus.nextOpen} />;
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 10, sm: 12, md: 16 }, mb: 20, px: { xs: 1.5, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width={300} height={60} sx={{ mx: 'auto', mb: 4 }} />
        <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }} sx={{ justifyContent: 'center' }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item key={i}>
              <Skeleton variant="rectangular" width={isMobile ? 140 : 220} height={isMobile ? 280 : 400} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <>
      {/* ─── Products Section Header ─── */}
      <Box sx={{ 
        px: { xs: 2, sm: 4, md: 6 }, 
        mt: { xs: 5, sm: 6, md: 8 }, 
        mb: { xs: 4, sm: 5, md: 6 } 
      }}>
        <Box sx={{
          position: 'relative',
          textAlign: 'center',
          py: { xs: 3, sm: 4, md: 5 },
        }}>
          {/* Top decorative line */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1.5, sm: 2, md: 3 },
            mb: { xs: 2, sm: 2.5, md: 3 }
          }}>
            <Box sx={{ 
              flex: 1, 
              maxWidth: { xs: 60, sm: 100, md: 140 },
              height: '2px', 
              background: `linear-gradient(to right, transparent, ${alpha(theme.palette.primary.main, 0.4)})`,
              borderRadius: '2px'
            }} />
            <Box sx={{
              width: { xs: 8, sm: 10, md: 12 },
              height: { xs: 8, sm: 10, md: 12 },
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Box sx={{
                width: { xs: 3, sm: 4 },
                height: { xs: 3, sm: 4 },
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
              }} />
            </Box>
            <Box sx={{ 
              flex: 1, 
              maxWidth: { xs: 60, sm: 100, md: 140 },
              height: '2px', 
              background: `linear-gradient(to left, transparent, ${alpha(theme.palette.primary.main, 0.4)})`,
              borderRadius: '2px'
            }} />
          </Box>

          {/* Branch image badge */}
          {(() => {
            const branchImageUrl = (() => {
              if (!selectedBranch) return null;
              const imageUrl = selectedBranch.ImageUrl || selectedBranch.imageUrl || selectedBranch.Image || selectedBranch.image;
              if (!imageUrl) return null;
              
              if (typeof imageUrl === 'object' && imageUrl !== null) {
                const url = imageUrl.medium || imageUrl.large || imageUrl.thumbnail;
                if (!url) return null;
                return url.startsWith('http') ? url : `${config.SITE_BASE_URL}${url}`;
              }
              
              if (typeof imageUrl === 'string') {
                if (imageUrl.startsWith('http') || imageUrl.startsWith('data:image')) return imageUrl;
                if (imageUrl.startsWith('/images/') || imageUrl.startsWith('/')) return `${config.SITE_BASE_URL}${imageUrl}`;
                return imageUrl;
              }
              
              return null;
            })();

            return (
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 64, sm: 80, md: 92 },
                height: { xs: 64, sm: 80, md: 92 },
                borderRadius: '50%',
                background: branchImageUrl 
                  ? 'transparent' 
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.light, 0.15)})`,
                border: `2.5px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                mb: { xs: 1.5, sm: 2 },
                position: 'relative',
                overflow: 'hidden',
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -8,
                  borderRadius: '50%',
                  border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
                  pointerEvents: 'none',
                }
              }}>
                {branchImageUrl ? (
                  <Box
                    component="img"
                    src={branchImageUrl}
                    alt={selectedBranch?.name || selectedBranch?.Name || 'Branch'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%',
                    }}
                    onError={(e) => { 
                      e.target.style.display = 'none'; 
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; 
                    }}
                  />
                ) : null}
                <Box
                  sx={{
                    display: branchImageUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <StorefrontIcon sx={{ 
                    fontSize: { xs: 28, sm: 36, md: 42 }, 
                    color: theme.palette.primary.main,
                    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`
                  }} />
                </Box>
              </Box>
            );
          })()}
          
          {/* Title */}
          <Typography 
            variant="h4" 
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: { xs: '0.5px', sm: '1px' },
              mb: { xs: 0.8, sm: 1 },
              lineHeight: 1.2,
            }}
          >
            {t('home.ourProducts', 'Our Products')}
          </Typography>

          {/* Subtitle */}
          <Typography 
            variant="body1" 
            sx={{
              color: alpha(theme.palette.text.secondary, 0.7),
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              fontWeight: 500,
              maxWidth: { xs: 280, sm: 400, md: 500 },
              mx: 'auto',
              lineHeight: 1.5,
              mb: { xs: 2, sm: 2.5, md: 3 }
            }}
          >
            {t('home.ourProductsSubtitle', 'Browse our carefully curated selection')}
          </Typography>

          {/* Bottom decorative line */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 0.8, sm: 1 },
          }}>
            <Box sx={{ 
              width: { xs: 30, sm: 50, md: 70 },
              height: '1.5px', 
              background: `linear-gradient(to right, transparent, ${alpha(theme.palette.primary.main, 0.3)})`,
              borderRadius: '2px'
            }} />
            <Box sx={{
              width: { xs: 50, sm: 70, md: 90 },
              height: { xs: '3px', sm: '3.5px', md: '4px' },
              borderRadius: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`
            }} />
            <Box sx={{ 
              width: { xs: 30, sm: 50, md: 70 },
              height: '1.5px', 
              background: `linear-gradient(to left, transparent, ${alpha(theme.palette.primary.main, 0.3)})`,
              borderRadius: '2px'
            }} />
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: 20, px: { xs: 1, sm: 3, md: 4 } }}>
        {productsByCategory.map((cat, index) => (
          <Box 
            key={cat.Id} 
            id={`category-section-${cat.Id}`} 
            sx={{ 
              mb: { xs: 5, sm: 8, md: 10 },
              animation: `${slideIn} 0.6s ease-out`,
              animationDelay: `${index * 0.1}s`,
              animationFillMode: 'backwards'
            }}
          >
            {/* Category Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 2.5, sm: 4, md: 5 }, position: 'relative' }}>
              <Box sx={{ position: 'relative', textAlign: 'center', px: { xs: 1.5, sm: 5 }, py: { xs: 0.8, sm: 2 } }}>
                <Typography 
                  variant="h4" 
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', sm: '2rem', md: '2.25rem' },
                    letterSpacing: '0.5px',
                    textTransform: 'capitalize',
                    position: 'relative',
                    display: 'inline-block',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: { xs: -4, sm: -8 },
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: { xs: '35px', sm: '60px' },
                      height: { xs: '2.5px', sm: '4px' },
                      borderRadius: '2px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                    }
                  }}
                >
                  {cat.CategoryName}
                </Typography>
              </Box>
            </Box>
            
            {/* Products Grid */}
            <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }} sx={{ direction: 'rtl', justifyContent: 'center' }}>
              {cat.products.map((prod, prodIndex) => {
                const imageUrls = getImageUrls(prod);
                
                const currentPrice = getPriceInCurrency(prod, selectedCurrency);
                const currencySymbol = getCurrencySymbol(selectedCurrency);
                const decimals = getCurrencyDecimals(selectedCurrency);
                
                let oldPrice = 0;
                let hasDiscount = false;
                
                if (prod.OldPrice) {
                  if (prod.OldCurrencyPrices) {
                    oldPrice = getPriceInCurrency(
                      { ...prod, CurrencyPrices: prod.OldCurrencyPrices },
                      selectedCurrency
                    );
                  } else {
                    const priceRatio = prod.OldPrice / (prod.Price || 1);
                    oldPrice = currentPrice * priceRatio;
                  }
                  hasDiscount = oldPrice > currentPrice;
                }
                
                const discountPercent = hasDiscount ? calculateDiscountPercentage(oldPrice, currentPrice) : 0;
                const isFavorite = favoriteIds.includes(prod.Id || prod.id);
                
                return (
                  <Grid 
                    item 
                    key={prod.Id} 
                    id={`product-${prod.Id}`}
                    sx={{
                      animation: `${slideIn} 0.5s ease-out`,
                      animationDelay: `${prodIndex * 0.05}s`,
                      animationFillMode: 'backwards'
                    }}
                  >
                    <Card 
                      sx={{ 
                        width: { xs: 140, sm: 200, md: 220 },
                        height: '100%',
                        borderRadius: { xs: 1.5, sm: 2 },
                        overflow: 'visible',
                        boxShadow: `0 ${isMobile ? '3px 10px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: 'background.paper',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': { 
                          transform: isMobile ? 'scale(0.98)' : 'translateY(-12px)',
                          boxShadow: `0 ${isMobile ? '5px 14px' : '20px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          '& .product-image': { transform: 'scale(1.1)' }
                        },
                        '&:active': {
                          transform: isMobile ? 'scale(0.95)' : 'translateY(-12px)',
                        }
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handleProductClick(prod)}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
                      >
                        {/* Image Section */}
                        <Box 
                          sx={{ 
                            position: 'relative', 
                            width: '100%', 
                            height: { xs: 100, sm: 160, md: 180 },
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            borderRadius: '12px 12px 0 0'
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={imageUrls.medium}
                            srcSet={`${imageUrls.thumbnail} 150w, ${imageUrls.medium} 400w, ${imageUrls.large} 1200w`}
                            sizes={isMobile ? "140px" : "(max-width: 960px) 200px, 220px"}
                            alt={prod.ProductName}
                            loading="lazy"
                            className="product-image"
                            sx={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover', 
                              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              cursor: 'pointer'
                            }}
                            onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                          />
                          {/* Share & QR Buttons */}
                          <Box sx={{ position: 'absolute', top: { xs: 4, sm: 10 }, left: { xs: 4, sm: 10 }, zIndex: 3, display: 'flex', gap: 0.5 }}>
                            <Tooltip title={t('product.share', 'Share')} arrow>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShareProduct(prod);
                                }}
                                sx={{
                                  bgcolor: 'white',
                                  boxShadow: 2,
                                  width: { xs: 24, sm: 32 },
                                  height: { xs: 24, sm: 32 },
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    transform: 'scale(1.1)',
                                  }
                                }}
                              >
                                <ShareIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={t('product.qrCode', 'QR Code')} arrow>
                              <IconButton
                                onClick={(e) => handleOpenProductQRModal(prod, e)}
                                sx={{
                                  bgcolor: 'white',
                                  boxShadow: 2,
                                  width: { xs: 24, sm: 32 },
                                  height: { xs: 24, sm: 32 },
                                  '&:hover': {
                                    bgcolor: 'secondary.main',
                                    color: 'white',
                                    transform: 'scale(1.1)',
                                  }
                                }}
                              >
                                <QrCode2Icon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Tooltip title={isFavorite ? t('favorites.remove') : t('favorites.add')} arrow placement="left">
                            <IconButton
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(prod.Id || prod.id); }}
                              sx={{
                                position: 'absolute',
                                top: { xs: 4, sm: 10 },
                                right: { xs: 4, sm: 10 },
                                bgcolor: 'white',
                                boxShadow: { xs: '0 2px 6px rgba(0,0,0,0.15)', sm: '0 4px 12px rgba(0,0,0,0.15)' },
                                zIndex: 3,
                                width: { xs: 28, sm: 40 },
                                height: { xs: 28, sm: 40 },
                                transition: 'all 0.3s ease',
                                border: `${isMobile ? '1.5px' : '2px'} solid ${isFavorite ? theme.palette.error.main : alpha(theme.palette.text.secondary, 0.2)}`,
                                '&:hover': {
                                  bgcolor: 'white',
                                  transform: 'scale(1.15)',
                                  boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                  borderColor: theme.palette.error.main
                                },
                                '&:active': {
                                  transform: 'scale(0.95)',
                                }
                              }}
                            >
                              {isFavorite ? (
                                <FavoriteIcon sx={{ fontSize: { xs: 16, sm: 22 }, color: theme.palette.error.main, animation: `${heartBeat} 0.3s ease` }} />
                              ) : (
                                <FavoriteBorderIcon sx={{ fontSize: { xs: 16, sm: 22 }, color: theme.palette.text.secondary }} />
                              )}
                            </IconButton>
                          </Tooltip>

                          {hasDiscount && (
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: { xs: 9, sm: 14 }, color: 'white !important' }} />}
                              label={`${discountPercent}%`}
                              sx={{
                                position: 'absolute',
                                top: { xs: 4, sm: 10 },
                                left: { xs: 4, sm: 10 },
                                bgcolor: theme.palette.error.main,
                                color: 'white',
                                fontWeight: 800,
                                fontSize: { xs: '9px', sm: '12px' },
                                height: { xs: 20, sm: 28 },
                                boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.error.main, 0.4)}`,
                                zIndex: 2,
                                animation: `${pulse} 2s infinite`,
                                '& .MuiChip-icon': {
                                  color: 'white',
                                  marginLeft: { xs: '2px', sm: '4px' }
                                }
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* Content Section */}
                        <CardContent sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Tooltip title={prod.ProductName} arrow>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontSize: { xs: '11px', sm: '15px', md: '16px' },
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                mb: { xs: 0.2, sm: 0.5 },
                                textAlign: 'center',
                                lineHeight: 1.3,
                                height: { xs: '2.3em', sm: '2.6em' },
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {prod.ProductName}
                            </Typography>
                          </Tooltip>

                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: { xs: '8px', sm: '11px' },
                              color: theme.palette.text.secondary,
                              mb: { xs: 0.8, sm: 1.5 },
                              textAlign: 'center',
                              lineHeight: 1.3,
                              height: { xs: '2.3em', sm: '2.8em' },
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {prod.Description}
                          </Typography>

                          {prod.Ingredients && prod.Ingredients.length > 0 && (
                            <Box 
                              sx={{ 
                                mb: { xs: 0.8, sm: 1.5 },
                                p: { xs: 0.5, sm: 1 },
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderRadius: { xs: 1, sm: 2 },
                                border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 0.5 }, mb: { xs: 0.2, sm: 0.3 }, justifyContent: 'center' }}>
                                <InfoOutlinedIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'primary.main' }} />
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                    fontSize: { xs: '7px', sm: '10px' },
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                  }}
                                >
                                  {t('products.ingredients', 'Ingredients')}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: theme.palette.text.secondary,
                                  fontSize: { xs: '7px', sm: '10px' },
                                  textAlign: 'center',
                                  display: 'block',
                                  lineHeight: 1.2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {Array.isArray(prod.Ingredients) ? prod.Ingredients.join(', ') : prod.Ingredients}
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ flexGrow: 1 }} />

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.4, sm: 1 }, mb: { xs: 1, sm: 2 } }}>
                            <Typography sx={{ color: theme.palette.primary.main, fontWeight: 800, fontSize: { xs: '12px', sm: '18px' } }}>
                              {currentPrice.toFixed(decimals)} {currencySymbol}
                            </Typography>
                            {hasDiscount && (
                              <Typography sx={{ textDecoration: 'line-through', color: theme.palette.text.disabled, fontSize: { xs: '9px', sm: '12px' }, fontWeight: 500 }}>
                                {oldPrice.toFixed(decimals)} {currencySymbol}
                              </Typography>
                            )}
                          </Box>

                          {/* Action Button: تسوق الان opening https://www.elorayem.com/product/{PRODUCT ID} in new tab */}
                          <Box sx={{ width: '100%', mt: 'auto' }}>
                            {prod.IsActive === false ? (
                              <Box 
                                sx={{
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  border: `${isMobile ? '1.5px' : '2px'} solid ${alpha(theme.palette.error.main, 0.3)}`,
                                  borderRadius: { xs: 1.5, sm: 3 },
                                  py: { xs: 0.8, sm: 1.5 },
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography sx={{ color: theme.palette.error.main, fontWeight: 700, fontSize: { xs: '9px', sm: '13px' }, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                                  {t('home.outOfStock', 'Out of Stock')}
                                </Typography>
                              </Box>
                            ) : (
                              <Button 
                                variant="contained"
                                fullWidth
                                onClick={(e) => handleOpenExternalProduct(prod, e)}
                                sx={{ 
                                  bgcolor: theme.palette.primary.main,
                                  borderRadius: { xs: 1.5, sm: 3 },
                                  color: theme.palette.primary.contrastText,
                                  py: { xs: 0.7, sm: 1.2 },
                                  fontSize: { xs: '9px', sm: '13px' },
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`,
                                  '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                    boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.4)}`,
                                    transform: isMobile ? 'none' : 'translateY(-2px)'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease',
                                  gap: { xs: 0.4, sm: 1 }
                                }}
                              >
                                <OpenInNewIcon sx={{ fontSize: { xs: 12, sm: 18 } }} />
                                تسوق الان
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
              
              {cat.products.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: { xs: 5, sm: 8 }, px: 2, borderRadius: { xs: 2, sm: 4 }, bgcolor: alpha(theme.palette.primary.main, 0.03), border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mb: 1, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                      {t('home.noProducts')}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
      </Container>

      {/* Product Details Modal */}
      <Modal
        open={!!selectedProduct}
        onClose={handleCloseProductModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: alpha(theme.palette.common.black, 0.75)
          }
        }}
      >
        <Fade in={!!selectedProduct}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: '850px' },
              maxHeight: { xs: '75vh', sm: '78vh', md: '82vh' },
              mt: { xs: 8, sm: 9, md: 10 },
              mb: { xs: 8, sm: 9, md: 10 },
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: { xs: 2, sm: 4 },
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`,
              outline: 'none',
              animation: `${slideIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`,
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: alpha(theme.palette.divider, 0.1),
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.primary.main, 0.3),
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.5)
                }
              }
            }}
          >
            {selectedProduct && (() => {
              const images = getAllProductImages(selectedProduct);
              const currentPrice = getPriceInCurrency(selectedProduct, selectedCurrency);
              const currencySymbol = getCurrencySymbol(selectedCurrency);
              const decimals = getCurrencyDecimals(selectedCurrency);
              
              let oldPrice = 0;
              let hasDiscount = false;
              
              if (selectedProduct.OldPrice) {
                if (selectedProduct.OldCurrencyPrices) {
                  oldPrice = getPriceInCurrency(
                    { ...selectedProduct, CurrencyPrices: selectedProduct.OldCurrencyPrices },
                    selectedCurrency
                  );
                } else {
                  const priceRatio = selectedProduct.OldPrice / (selectedProduct.Price || 1);
                  oldPrice = currentPrice * priceRatio;
                }
                hasDiscount = oldPrice > currentPrice;
              }
              
              const discountPercent = hasDiscount ? calculateDiscountPercentage(oldPrice, currentPrice) : 0;
              const isFavorite = favoriteIds.includes(selectedProduct.Id || selectedProduct.id);

              return (
                <>
                  {/* Close Button */}
                  <IconButton
                    onClick={handleCloseProductModal}
                    sx={{
                      position: 'absolute',
                      top: { xs: 8, sm: 16 },
                      right: { xs: 8, sm: 16 },
                      bgcolor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(10px)',
                      zIndex: 10,
                      boxShadow: 3,
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                        transform: 'rotate(90deg) scale(1.1)',
                        boxShadow: 6
                      },
                      '&:active': {
                        transform: 'scale(0.9)'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </IconButton>

                  {/* Content */}
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    {/* Left Side - Auto-Sliding Image Gallery */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: { xs: 300, sm: 400, md: 450 },
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
                          }}
                        >
                          {images.length > 0 ? (
                            <Box
                              component="img"
                              src={images[currentImageIndex]}
                              alt={`${selectedProduct.ProductName} - Image ${currentImageIndex + 1}`}
                              onClick={(e) => handleImageClick(images[currentImageIndex], e)}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                cursor: 'zoom-in',
                                transition: 'opacity 0.6s ease-in-out, transform 0.3s ease',
                                animation: `${fadeIn} 0.6s ease-in-out`,
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                              onError={(e) => { 
                                e.target.src = '/logo-storesye.png'; 
                              }}
                              key={`image-${currentImageIndex}`}
                            />
                          ) : (
                            <Box
                              component="img"
                              src="/logo-storesye.png"
                              alt="No image available"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          )}

                          {/* Zoom Indicator */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              right: 16,
                              bgcolor: alpha(theme.palette.common.black, 0.6),
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backdropFilter: 'blur(10px)',
                              cursor: 'zoom-in',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.common.black, 0.8),
                                transform: 'scale(1.05)'
                              }
                            }}
                            onClick={(e) => images.length > 0 && handleImageClick(images[currentImageIndex], e)}
                          >
                            <ZoomInIcon sx={{ fontSize: 18, color: 'white' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                              {t('products.clickToZoom', 'Click to Zoom')}
                            </Typography>
                          </Box>

                          {images.length > 1 && (
                            <Tooltip 
                              title={autoSlideInterval ? t('products.pauseSlideshow', 'Pause') : t('products.playSlideshow', 'Play')} 
                              arrow
                            >
                              <IconButton
                                onClick={toggleAutoSlide}
                                sx={{
                                  position: 'absolute',
                                  bottom: 16,
                                  left: 16,
                                  bgcolor: alpha(theme.palette.common.black, 0.6),
                                  backdropFilter: 'blur(10px)',
                                  color: 'white',
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.common.black, 0.8),
                                    transform: 'scale(1.1)'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                              >
                                {autoSlideInterval ? (
                                  <PauseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                ) : (
                                  <PlayArrowIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}

                          {images.length > 1 && (
                            <>
                              <IconButton
                                onClick={handlePrevImage}
                                sx={{
                                  position: 'absolute',
                                  left: { xs: 8, sm: 16 },
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: 3,
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: 6
                                  },
                                  '&:active': {
                                    transform: 'translateY(-50%) scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <NavigateBeforeIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                              </IconButton>
                              <IconButton
                                onClick={handleNextImage}
                                sx={{
                                  position: 'absolute',
                                  right: { xs: 8, sm: 16 },
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: 3,
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: 6
                                  },
                                  '&:active': {
                                    transform: 'translateY(-50%) scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <NavigateNextIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                              </IconButton>
                            </>
                          )}

                          {hasDiscount && (
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                              label={`${discountPercent}% ${t('products.off', 'OFF')}`}
                              sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                bgcolor: theme.palette.error.main,
                                color: 'white',
                                fontWeight: 800,
                                fontSize: '14px',
                                height: 32,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
                                zIndex: 2,
                                '& .MuiChip-icon': {
                                  color: 'white'
                                }
                              }}
                            />
                          )}

                          {images.length > 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                bgcolor: alpha(theme.palette.common.black, 0.6),
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.5,
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                                {currentImageIndex + 1} / {images.length}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {images.length > 1 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              mt: 2,
                              overflowX: 'auto',
                              pb: 1,
                              '&::-webkit-scrollbar': {
                                height: '6px'
                              },
                              '&::-webkit-scrollbar-track': {
                                bgcolor: alpha(theme.palette.divider, 0.1),
                                borderRadius: '10px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                bgcolor: alpha(theme.palette.primary.main, 0.3),
                                borderRadius: '10px',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.5)
                                }
                              }
                            }}
                          >
                            {images.map((img, index) => (
                              <Box
                                key={`thumb-${index}`}
                                onClick={() => {
                                  setCurrentImageIndex(index);
                                  if (autoSlideInterval) {
                                    clearInterval(autoSlideInterval);
                                    const newInterval = setInterval(() => {
                                      setCurrentImageIndex((prev) => (prev + 1) % images.length);
                                    }, 3000);
                                    setAutoSlideInterval(newInterval);
                                  }
                                }}
                                sx={{
                                  minWidth: { xs: 60, sm: 80 },
                                  height: { xs: 60, sm: 80 },
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  border: `3px solid ${index === currentImageIndex ? theme.palette.primary.main : 'transparent'}`,
                                  opacity: index === currentImageIndex ? 1 : 0.6,
                                  transition: 'all 0.3s ease',
                                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                                  position: 'relative',
                                  '&:hover': {
                                    opacity: 1,
                                    transform: 'scale(1.05)',
                                    boxShadow: 3
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={img}
                                  alt={`Thumbnail ${index + 1}`}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => { 
                                    e.target.src = '/logo-storesye.png'; 
                                  }}
                                />
                                {index === currentImageIndex && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: '3px',
                                      bgcolor: theme.palette.primary.main,
                                      animation: `${slideTransition} 0.3s ease`
                                    }}
                                  />
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Grid>

                    {/* Right Side - Product Details */}
                    <Grid item xs={12} md={6}>
                      <Stack spacing={{ xs: 2, sm: 3 }}>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                              color: theme.palette.text.primary,
                              mb: 1,
                              lineHeight: 1.2
                            }}
                          >
                            {selectedProduct.ProductName}
                          </Typography>

                          <Chip
                            label={categories.find(c => c.Id === selectedProduct.CategoryId)?.CategoryName || t('products.category', 'Category')}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}
                          />
                        </Box>

                        <Divider />

                        <Box>
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 900,
                                color: theme.palette.primary.main,
                                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }
                              }}
                            >
                              {currentPrice.toFixed(decimals)} {currencySymbol}
                            </Typography>
                            {hasDiscount && (
                              <Typography
                                variant="h5"
                                sx={{
                                  textDecoration: 'line-through',
                                  color: theme.palette.text.disabled,
                                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                  fontWeight: 600
                                }}
                              >
                                {oldPrice.toFixed(decimals)} {currencySymbol}
                              </Typography>
                            )}
                          </Stack>
                          {hasDiscount && (
                            <Typography
                              sx={{
                                color: theme.palette.success.main,
                                fontWeight: 700,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }}
                            >
                              {t('products.youSave', 'You save')} {(oldPrice - currentPrice).toFixed(decimals)} {currencySymbol} ({discountPercent}%)
                            </Typography>
                          )}
                        </Box>

                        <Divider />

                        {selectedProduct.Description && (
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                color: theme.palette.text.primary
                              }}
                            >
                              {t('products.description', 'Description')}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.8,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }}
                            >
                              {selectedProduct.Description}
                            </Typography>
                          </Box>
                        )}

                        {selectedProduct.Ingredients && selectedProduct.Ingredients.length > 0 && (
                          <Box
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              borderRadius: 2,
                              border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                  color: theme.palette.primary.main
                                }}
                              >
                                {t('products.ingredients', 'Ingredients')}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.6,
                                fontSize: { xs: '0.85rem', sm: '0.95rem' }
                              }}
                            >
                              {Array.isArray(selectedProduct.Ingredients) 
                                ? selectedProduct.Ingredients.join(', ') 
                                : selectedProduct.Ingredients}
                            </Typography>
                          </Box>
                        )}

                        {/* Product Colors Selection for viewing/selecting color preview if needed */}
                        {selectedProduct.ProductColors && Array.isArray(selectedProduct.ProductColors) && selectedProduct.ProductColors.length > 0 && (
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 2,
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                color: theme.palette.text.primary,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              🎨 {t('products.selectColor', 'Select Color')}
                              {selectedColor && (
                                <Chip
                                  label={selectedColor.colorName}
                                  size="small"
                                  color="secondary"
                                  sx={{ fontSize: '0.8rem' }}
                                />
                              )}
                            </Typography>
                            
                            <Stack
                              direction="row"
                              spacing={1.5}
                              sx={{
                                overflowX: 'auto',
                                pb: 1,
                                '&::-webkit-scrollbar': {
                                  height: '6px'
                                },
                                '&::-webkit-scrollbar-track': {
                                  bgcolor: alpha(theme.palette.divider, 0.1),
                                  borderRadius: '10px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  bgcolor: alpha(theme.palette.secondary.main, 0.3),
                                  borderRadius: '10px',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.secondary.main, 0.5)
                                  }
                                }
                              }}
                            >
                              {selectedProduct.ProductColors.map((color, index) => {
                                const isSelected = selectedColor?.colorName === color.colorName;
                                
                                return (
                                  <Tooltip key={index} title={color.colorName} arrow>
                                    <Box
                                      onClick={() => {
                                        setSelectedColor(color);
                                      }}
                                      sx={{
                                        minWidth: { xs: 70, sm: 90 },
                                        height: { xs: 70, sm: 90 },
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        border: `3px solid ${isSelected ? theme.palette.secondary.main : alpha(theme.palette.divider, 0.3)}`,
                                        position: 'relative',
                                        transition: 'all 0.3s ease',
                                        bgcolor: color.colorImage ? 'transparent' : alpha(theme.palette.secondary.main, 0.1),
                                        boxShadow: isSelected ? `0 4px 12px ${alpha(theme.palette.secondary.main, 0.4)}` : 2,
                                        '&:hover': {
                                          transform: 'scale(1.05)',
                                          boxShadow: 4,
                                          borderColor: theme.palette.secondary.main
                                        },
                                        '&:active': {
                                          transform: 'scale(0.95)'
                                        }
                                      }}
                                    >
                                      {color.colorImage ? (
                                        (() => {
                                          let colorImgUrl = null;
                                          if (typeof color.colorImage === 'object' && color.colorImage !== null) {
                                            colorImgUrl = color.colorImage.medium || color.colorImage.large || color.colorImage.thumbnail;
                                          } else if (typeof color.colorImage === 'string') {
                                            colorImgUrl = color.colorImage.startsWith('http') 
                                              ? color.colorImage 
                                              : `${config.SITE_BASE_URL}${color.colorImage}`;
                                          }
                                          return colorImgUrl ? (
                                            <Box
                                              component="img"
                                              src={colorImgUrl}
                                              alt={color.colorName}
                                              sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                              }}
                                              onError={(e) => { 
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontSize: '2rem' }}>🎨</Box>
                                          );
                                        })()
                                      ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontSize: '2rem' }}>🎨</Box>
                                      )}
                                      
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          left: 0,
                                          right: 0,
                                          bgcolor: isSelected 
                                            ? theme.palette.secondary.main 
                                            : alpha(theme.palette.common.black, 0.7),
                                          color: 'white',
                                          py: 0.5,
                                          px: 0.5,
                                          textAlign: 'center'
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                            fontWeight: isSelected ? 700 : 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {color.colorName}
                                        </Typography>
                                      </Box>
                                      
                                      {isSelected && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            bgcolor: theme.palette.secondary.main,
                                            borderRadius: '50%',
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: 2
                                          }}
                                        >
                                          <CheckCircleIcon sx={{ fontSize: 18, color: 'white' }} />
                                        </Box>
                                      )}
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}

                        <Divider />

                        {/* Action Buttons */}
                        <Stack spacing={2}>
                          {/* Favorite Button */}
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => toggleFavorite(selectedProduct.Id || selectedProduct.id)}
                            startIcon={
                              isFavorite ? (
                                <FavoriteIcon sx={{ color: theme.palette.error.main }} />
                              ) : (
                                <FavoriteBorderIcon />
                              )
                            }
                            sx={{
                              borderRadius: 3,
                              py: { xs: 1.2, sm: 1.5 },
                              fontSize: { xs: '0.95rem', sm: '1rem' },
                              fontWeight: 700,
                              borderWidth: 2,
                              borderColor: isFavorite ? theme.palette.error.main : theme.palette.divider,
                              color: isFavorite ? theme.palette.error.main : theme.palette.text.primary,
                              '&:hover': {
                                borderWidth: 2,
                                borderColor: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {isFavorite ? t('favorites.removeFromFavorites', 'Remove from Favorites') : t('favorites.addToFavorites', 'Add to Favorites')}
                          </Button>

                          {/* تسوق الان Button in modal */}
                          {selectedProduct.IsActive === false ? (
                            <Box
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                borderRadius: 3,
                                py: { xs: 1.5, sm: 2 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.error.main,
                                  fontWeight: 700,
                                  fontSize: { xs: '1rem', sm: '1.15rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {t('home.outOfStock', 'Out of Stock')}
                              </Typography>
                            </Box>
                          ) : (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={(e) => handleOpenExternalProduct(selectedProduct, e)}
                              startIcon={<OpenInNewIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                              sx={{
                                bgcolor: theme.palette.primary.main,
                                borderRadius: 3,
                                py: { xs: 1.5, sm: 2 },
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                  bgcolor: theme.palette.primary.dark,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                                },
                                '&:active': {
                                  transform: 'scale(0.98)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              تسوق الان
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              );
            })()}
          </Box>
        </Fade>
      </Modal>

      {/* Image Zoom Modal */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: alpha(theme.palette.common.black, 0.85)
          }
        }}
      >
        <Fade in={!!selectedImage}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '95vw',
              maxHeight: '95vh',
              outline: 'none',
              borderRadius: { xs: 2, sm: 4 },
              overflow: 'hidden',
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`,
              animation: `${slideIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(10px)',
                zIndex: 1,
                boxShadow: 3,
                width: { xs: 32, sm: 44 },
                height: { xs: 32, sm: 44 },
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                  transform: 'rotate(90deg) scale(1.1)'
                },
                '&:active': {
                  transform: 'scale(0.9)'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </IconButton>
            <Box
              component="img"
              src={selectedImage}
              alt="Product"
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '95vh',
                objectFit: 'contain',
                borderRadius: { xs: 2, sm: 4 }
              }}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: 4,
            minWidth: { xs: 200, sm: 300 },
            fontSize: { xs: '0.8rem', sm: '0.95rem' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Product QR Code Modal */}
      <Modal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: alpha(theme.palette.common.black, 0.7),
            }
          }
        }}
        sx={{ zIndex: 1600 }}
      >
        <Fade in={qrModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500, md: 600 },
              maxHeight: '85vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: { xs: 2, sm: 3, md: 4 },
              boxShadow: 24,
              p: { xs: 3, sm: 4, md: 5 },
              outline: 'none',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: alpha(theme.palette.divider, 0.1),
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.primary.main, 0.3),
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.5)
                }
              }
            }}
          >
            {selectedProductForQR && (
              <>
                <IconButton
                  onClick={() => setQrModalOpen(false)}
                  sx={{
                    position: 'absolute',
                    top: { xs: 12, sm: 16 },
                    right: { xs: 12, sm: 16 },
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    zIndex: 1,
                    '&:hover': {
                      bgcolor: theme.palette.error.main,
                      color: 'white'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: 'primary.main',
                    textAlign: 'center',
                    mb: 1,
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    pr: { xs: 5, sm: 6 }
                  }}
                >
                  {selectedProductForQR.ProductName}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                    mb: 3,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' }
                  }}
                >
                  {t('product.shareQRTitle', 'Share this product with others')}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                    p: { xs: 2, sm: 3 },
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  <QRCodeCanvas
                    id="product-qr-code-canvas"
                    value={generateProductLink(selectedProductForQR.Id || selectedProductForQR.id)}
                    size={isMobile ? 200 : 256}
                    level="H"
                    includeMargin={true}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 2,
                    mb: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}
                >
                  <LinkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.75rem', sm: '0.85rem' }
                    }}
                  >
                    {generateProductLink(selectedProductForQR.Id || selectedProductForQR.id)}
                  </Typography>
                  <IconButton
                    onClick={() => handleCopyProductLink(selectedProductForQR)}
                    size="small"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadProductQR}
                    sx={{
                      py: { xs: 1.2, sm: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    {t('product.downloadQR', 'Download QR Code')}
                  </Button>

                  <Divider>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {t('product.orShareVia', 'Or share via')}
                    </Typography>
                  </Divider>

                  <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
                    <Tooltip title="WhatsApp" arrow>
                      <IconButton
                        onClick={() => handleShareProductWhatsApp(selectedProductForQR)}
                        sx={{
                          bgcolor: '#25D366',
                          color: 'white',
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          '&:hover': {
                            bgcolor: '#128C7E',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <WhatsAppIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Facebook" arrow>
                      <IconButton
                        onClick={() => handleShareProductFacebook(selectedProductForQR)}
                        sx={{
                          bgcolor: '#1877F2',
                          color: 'white',
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          '&:hover': {
                            bgcolor: '#166FE5',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <FacebookIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Twitter" arrow>
                      <IconButton
                        onClick={() => handleShareProductTwitter(selectedProductForQR)}
                        sx={{
                          bgcolor: '#1DA1F2',
                          color: 'white',
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          '&:hover': {
                            bgcolor: '#1A91DA',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <TwitterIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('product.copyLink', 'Copy Link')} arrow>
                      <IconButton
                        onClick={() => handleCopyProductLink(selectedProductForQR)}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

export default Products;
